import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AppMaterialModule } from '../../../../app-material.module';
import { ReleaseNotesService } from '../../../../shared/services/release-notes/release-notes.service';
import { ReleaseNotesUnreadService } from '../../../../shared/services/release-notes/release-notes-unread.service';
import { IReleaseNote } from '../../../../shared/models/release-note.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UrlEncurtadaPipe } from '../../../../shared/pipes/url-encurtada.pipe';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalNoticeDialogComponent } from '../../../../shared/components/dialogs/modal-notice-dialog/modal-notice-dialog.component';
import { toReleaseNoteVideoHtml } from '../../../../shared/utils/release-note-video.utils';

@Component({
    selector: 'app-release-notes',
    standalone: true,
    imports: [CommonModule, AppMaterialModule, RouterLink, UrlEncurtadaPipe, TranslocoPipe],
    templateUrl: './release-notes.component.html',
    styleUrl: './release-notes.component.scss',
})
export class ReleaseNotesComponent implements OnInit {
    releaseNotes: IReleaseNote[] = [];
    filteredNotes: IReleaseNote[] = [];
    isLoading = false;
    selectedFilter: 'alerta' | 'aviso' | 'atualizacao' = 'aviso';

    @Input() onClose?: () => void;

    constructor(
        private releaseNotesService: ReleaseNotesService,
        private releaseNotesUnreadService: ReleaseNotesUnreadService,
        private domSanitizer: DomSanitizer,
        private dialog: MatDialog,
        private destroyRef: DestroyRef,
        private transloco: TranslocoService,
    ) {}

    ngOnInit(): void {
        this.initReleaseNotes();
    }

    private initReleaseNotes(): void {
        this.loadReleaseNotes();
    }

    closePanel(): void {
        this.onClose?.();
    }

    handleContentClick(event: MouseEvent): void {
        const target = (event.target as HTMLElement).closest('.trigger-video-popup');
        if (!target) {
            return;
        }

        event.preventDefault();
        this.dialog.open(ModalNoticeDialogComponent, {
            data: {
                titulo: this.transloco.translate('dashboard.releaseNotes.videoDialogTitle'),
                subtitulo: this.transloco.translate('dashboard.releaseNotes.videoDialogSubtitle'),
                videoUrl: 'https://www.youtube.com/embed/jQuoC-mxePo?rel=0&modestbranding=1',
            },
        });
    }

    loadReleaseNotes(): void {
        this.isLoading = true;
        this.releaseNotesService
            .getAll()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (notes) =>
                    this.handleReleaseNotesLoaded(
                        notes.filter((note) => note.tipo === 'aviso' && note.placement !== 'carrousel-warning'),
                    ),
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    private handleReleaseNotesLoaded(notes: IReleaseNote[]): void {
        const videoTitle = this.transloco.translate('dashboard.releaseNotes.videoTitle');
        this.releaseNotes = this.releaseNotesService.filterVisibleForUser(notes).map((note) => ({
            ...note,
            video: note.video ? toReleaseNoteVideoHtml(this.domSanitizer, note.video, videoTitle) : note.video,
        }));
        this.applyFilter();
        this.isLoading = false;
        this.releaseNotesUnreadService.markNotesAsSeen(this.releaseNotes);
    }

    applyFilter(): void {
        const filtered = this.releaseNotes.filter((note) => note.tipo === this.selectedFilter);
        this.filteredNotes = filtered.slice(0, 3);
    }

    setFilter(filter: 'alerta' | 'aviso' | 'atualizacao'): void {
        this.selectedFilter = filter;
        this.applyFilter();
    }

    sanitizeHtml(html: string): SafeHtml {
        return this.domSanitizer.bypassSecurityTrustHtml(html);
    }

    getTipoColor(tipo?: string): string {
        switch (tipo) {
            case 'alerta':
                return '#ef4444';
            case 'aviso':
                return '#f59e0b';
            case 'atualizacao':
                return '#10b981';
            default:
                return '#6b7280';
        }
    }

    getTipoBackgroundColor(tipo?: string): string {
        switch (tipo) {
            case 'alerta':
                return 'rgba(239, 68, 68, 0.1)';
            case 'aviso':
                return 'rgba(245, 158, 11, 0.1)';
            case 'atualizacao':
                return 'rgba(16, 185, 129, 0.1)';
            default:
                return 'rgba(107, 114, 128, 0.1)';
        }
    }

    getTipoLabelKey(tipo?: string): string {
        switch (tipo) {
            case 'alerta':
                return 'dashboard.releaseNotes.type.alert';
            case 'aviso':
                return 'dashboard.releaseNotes.type.notice';
            case 'atualizacao':
                return 'dashboard.releaseNotes.type.update';
            default:
                return 'dashboard.releaseNotes.type.note';
        }
    }

    getInitials(name: string | null | undefined): string {
        if (!name?.trim()) {
            return '?';
        }

        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }
}
